(function () {
  'use strict';

  /*
    Touch active card, for every horizontal card row on the site.

    On desktop the pointer says which card you mean. On touch nothing does,
    so the card centred in its scroller says it instead and wears the frame
    the pointer would have given it - see the (hover: none) block in
    snippets/bc-layout-tokens.liquid, beside the hover rules it mirrors.

    The rows are found rather than listed. Every card in every row carries
    the shared .bc-card class, so a scroller is simply the parent of a
    .bc-card that scrolls horizontally: fourteen rows on the homepage today -
    cue tiles, cases, cloth, tips, balls, ball sets, rail rubber, chalk -
    and any row added later is picked up without editing this file.

    One observer PER ROW. A single observer rooted on one scroller would
    measure the other rows' cards against a box they do not live in, leaving
    one framed card across the whole page instead of one in each row.

    The observer's root is the scroller and its rootMargin collapses that
    root to a vertical line down the centre (-50% left and right), so a
    callback fires exactly when a card crosses the middle. During a flick
    two cards can touch that line in the same batch and only one should be
    framed, so the callback picks the one whose centre is nearest.

    It picks it from the ENTRIES, and that is the whole point of this file's
    current shape. The first version re-measured instead: every callback
    called getBoundingClientRect on the scroller and on each of its cards,
    which is a forced synchronous layout in the middle of a swipe, on a page
    with fifteen rows and sixty-five cards. An IntersectionObserver entry
    already carries boundingClientRect and rootBounds, measured by the
    browser when it decided to deliver the entry, so the same arithmetic
    costs nothing. Nothing in this file may read layout during a scroll.

    Two more things keep the swipe cheap. The class is written only when the
    winner actually changes - the old code toggled it on every card of the
    row on every callback, which invalidated style for the whole row several
    times a flick. And callbacks are coalesced into one rAF, so a batch of
    crossings produces a single write pass per frame.

    When no card covers the line - the gap between two cards is crossing it -
    the current card keeps the frame rather than losing it, because dropping
    it for those frames reads as a flicker between cards.
  */

  if (window.__bcTouchActiveCardBound) return;
  window.__bcTouchActiveCardBound = true;

  var ACTIVE = 'is-touch-active';
  var touchQuery = window.matchMedia ? window.matchMedia('(hover: none)') : null;

  var observed = new WeakMap();
  var observers = [];
  // Kept alongside the observers so disable() can reach each row's pending
  // frame; a WeakMap cannot be enumerated.
  var scrollers = [];
  var rescanTimer = 0;

  function isScroller(el) {
    if (!el || el.nodeType !== 1) return false;
    var overflowX = window.getComputedStyle(el).overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  function findScrollers() {
    var found = [];
    var seen = [];
    document.querySelectorAll('.bc-card').forEach(function (card) {
      var row = card.parentElement;
      if (!row || seen.indexOf(row) !== -1) return;
      seen.push(row);
      if (isScroller(row)) found.push(row);
    });
    return found;
  }

  // Per row: which cards are on the centre line right now, with the rects the
  // observer handed us, plus the card currently wearing the frame.
  var state = new WeakMap();

  function stateFor(scroller) {
    var s = state.get(scroller);
    if (!s) {
      s = { hits: new Map(), rootCentre: null, current: null, frame: 0 };
      state.set(scroller, s);
    }
    return s;
  }

  function settle(scroller) {
    var s = stateFor(scroller);
    s.frame = 0;
    // The row may have been let go between the frame being asked for and the
    // frame running - a phone-width window widened to a desktop one is the
    // ordinary way. Writing the class then would put a touch frame on a
    // desktop card, and nothing would ever take it off again.
    if (!observed.has(scroller)) return;
    if (s.rootCentre === null) return;
    var best = null;
    var bestDistance = Infinity;
    s.hits.forEach(function (rect, card) {
      var d = Math.abs(rect.left + rect.width / 2 - s.rootCentre);
      if (d < bestDistance) { bestDistance = d; best = card; }
    });
    // Nothing on the line: the gap between two cards is crossing it, so keep
    // the frame where it is rather than blinking it off for those frames.
    if (!best || best === s.current) return;
    if (s.current) s.current.classList.remove(ACTIVE);
    best.classList.add(ACTIVE);
    s.current = best;
  }

  function schedule(scroller) {
    var s = stateFor(scroller);
    if (s.frame) return;
    s.frame = window.requestAnimationFrame(function () { settle(scroller); });
  }

  function enable() {
    if (!('IntersectionObserver' in window)) return;
    findScrollers().forEach(function (scroller) {
      if (observed.has(scroller)) return;
      var observer = new IntersectionObserver(function (entries) {
        var s = stateFor(scroller);
        entries.forEach(function (entry) {
          // Free: the browser measured these when it queued the entry.
          if (entry.rootBounds) {
            s.rootCentre = entry.rootBounds.left + entry.rootBounds.width / 2;
          }
          if (entry.isIntersecting) {
            s.hits.set(entry.target, entry.boundingClientRect);
          } else {
            s.hits.delete(entry.target);
          }
        });
        schedule(scroller);
      }, {
        root: scroller,
        rootMargin: '0px -50% 0px -50%',
        threshold: 0
      });
      scroller.querySelectorAll(':scope > .bc-card').forEach(function (card) {
        observer.observe(card);
      });
      observed.set(scroller, observer);
      observers.push(observer);
      scrollers.push(scroller);
      // No first pass here: the observer delivers an initial batch for
      // everything it observes, which is what fills hits and rootCentre.
    });
  }

  function disable() {
    // Cancel first. Disconnecting an observer does not cancel a frame that
    // one of its callbacks already asked for, and that frame would re-add
    // the class after the classes below were cleared.
    scrollers.forEach(function (scroller) {
      var s = state.get(scroller);
      if (s && s.frame) {
        window.cancelAnimationFrame(s.frame);
        s.frame = 0;
      }
    });
    scrollers = [];
    observers.forEach(function (observer) { observer.disconnect(); });
    observers = [];
    observed = new WeakMap();
    // The per-row state goes with the observers that fill it, or a re-enable
    // would start with stale rects and a card it thinks is still framed.
    state = new WeakMap();
    document.querySelectorAll('.' + ACTIVE).forEach(function (card) {
      card.classList.remove(ACTIVE);
    });
  }

  function sync() {
    if (!touchQuery || touchQuery.matches) enable();
    else disable();
  }

  function scheduleRescan() {
    window.clearTimeout(rescanTimer);
    rescanTimer = window.setTimeout(sync, 200);
  }

  sync();

  if (touchQuery) {
    if (touchQuery.addEventListener) touchQuery.addEventListener('change', sync);
    else if (touchQuery.addListener) touchQuery.addListener(sync);
  }

  /*
    Several rows arrive after first paint - the cloth rows are fetched
    lazily, and the theme editor re-renders sections in place. A debounced
    rescan picks those up; enable() skips rows it has already observed, so
    running it again is cheap and idempotent.
  */
  document.addEventListener('shopify:section:load', scheduleRescan);

  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('bc-card')) { scheduleRescan(); return; }
          if (node.querySelector && node.querySelector('.bc-card')) { scheduleRescan(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
